import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = [
        "select",
        "sidebarSection",
        "sidebarSectionResponsive",
    ];

    static values = {
        locale: {
            type: String,
            default: 'en'
        }
    }

    connect() {
        if (!this.hasSelectTarget) {
            if (this.hasSidebarSectionTarget) {
                this.sidebarSectionTarget.classList.add("d-none");
            }

            if (this.hasSidebarSectionResponsiveTarget) {
                this.sidebarSectionResponsiveTarget.classList.add("d-none");
            }

            return;
        }

        const formGroup = this.selectTarget.closest(".form-group");

        if (formGroup && formGroup.parentNode) {

            const hr = document.createElement("hr");

            const responsiveSidebar = document.createElement("div");
            responsiveSidebar.classList.add("modal-sidebar-section-responsive");
            responsiveSidebar.setAttribute("data-region-target", "sidebarSectionResponsive");

            const selectParentTarget = this.selectTarget.parentNode;
            if (selectParentTarget) {
                selectParentTarget.appendChild(hr);
            }
            this.insertAfter(formGroup.parentNode, responsiveSidebar, formGroup);
        }
        const selected = this.selectTarget.selectedOptions[0];
        if (selected) {
            this.loadContent(selected.value);
        }
    }

    select() {
        const selected = this.selectTarget.selectedOptions[0];
        if (selected) {
            this.loadContent(selected.value);
        }
    }

    #contactInfoPath(contentId) {
        const sanitizedId = parseInt(contentId, 10);
        if (isNaN(sanitizedId) || sanitizedId <= 0) {
            return null;
        }
        const locale = (/^[a-z]{2}$/i).test(this.localeValue) ? this.localeValue.toLowerCase() : 'en';
        const pathname = `/${locale}/form/getContactInfo/${sanitizedId}`;
        if (!/^\/[a-z]{2}\/form\/getContactInfo\/\d+$/.test(pathname)) {
            return null;
        }
        try {
            const url = new URL(pathname, window.location.origin);
            if (url.origin !== window.location.origin) {
                return null;
            }
            return url.pathname;
        } catch {
            return null;
        }
    }

    async loadContent(contentId) {
        try {
            if (!contentId) {
                return;
            }
            const path = this.#contactInfoPath(contentId);
            if (!path) {
                return;
            }
            const response = await fetch(path, { credentials: 'same-origin' });
            const data = await response.json();

            if (data.error) {
                return;
            }

            if (this.hasSidebarSectionTarget) {
                this.#setSafeHtml(this.sidebarSectionTarget, data.sidebar);
            }

            if (this.hasSidebarSectionResponsiveTarget) {
                this.#setSafeHtml(this.sidebarSectionResponsiveTarget, data.mobileSidebar);
            }

        } catch (e) { }
    }

    #setSafeHtml(targetEl, html) {
        if (!targetEl) {
            return;
        }
        if (typeof html !== 'string' || html.trim() === '') {
            targetEl.replaceChildren();
            return;
        }
        const fragment = this.#sanitizeToFragment(html);
        targetEl.replaceChildren(fragment);
    }

    #sanitizeToFragment(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const blockedTags = new Set([
            'script',
            'iframe',
            'object',
            'embed',
            'link',
            'meta',
            'base',
            'style',
        ]);

        doc.querySelectorAll(Array.from(blockedTags).join(',')).forEach((el) => el.remove());

        const elements = doc.body.querySelectorAll('*');
        elements.forEach((el) => {
            Array.from(el.attributes).forEach((attr) => {
                const name = attr.name.toLowerCase();
                const value = (attr.value ?? '').trim();

                if (name.startsWith('on')) {
                    el.removeAttribute(attr.name);
                    return;
                }

                if (name === 'srcdoc') {
                    el.removeAttribute(attr.name);
                    return;
                }

                if (name === 'href' || name === 'src' || name === 'xlink:href') {
                    const v = value.toLowerCase();
                    if (v.startsWith('javascript:') || v.startsWith('data:text/html')) {
                        el.removeAttribute(attr.name);
                        return;
                    }
                }
            });
        });

        const fragment = document.createDocumentFragment();
        Array.from(doc.body.childNodes).forEach((node) => {
            fragment.appendChild(document.importNode(node, true));
        });
        return fragment;
    }

    insertAfter(parent, newNode, referenceNode) {
        if (referenceNode.nextSibling) {
            parent.insertBefore(newNode, referenceNode.nextSibling);
        } else {
            parent.appendChild(newNode);
        }
    }
}
